
export enum ServiceStatus {
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
  DEGRADED = 'DEGRADED',
  OVERLOADED = 'OVERLOADED'
}

export type ProviderCategory = 'LLM' | 'VLM' | 'ASR' | 'TTS';
export type AIProviderType = 'GEMINI' | 'OPENAI' | 'LOCAL' | 'CUSTOM';

export interface ProviderConfig {
  type: AIProviderType;
  baseUrl?: string;
  apiKey?: string;
  modelId: string;
  voiceId?: string; // Specific to TTS
}

export interface ModelConfig {
  id: string;
  name: string;
  category: ProviderCategory;
  providerType: string;
  endpoint: string;
  contextLength: number;
  isPrimary: boolean;
  status: ServiceStatus;
  autoUnloadMinutes: number;
  parameters: {
    temperature: number;
    topP: number;
  };
}

export interface APIClient {
  id: string;
  name: string;
  apiKey?: string;
  tokenBalance: number;
  tokenLimit: number;
  usageCount: number;
  lastActive: string;
  isActive: boolean;
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
}

export interface AIIntelligenceConfig {
  vision: ProviderConfig;
  asr: ProviderConfig;
  tts: ProviderConfig;
  agent: ProviderConfig; // Added for local Agent/Chat support
  activeProvider: AIProviderType;
}

export interface KernelSettings {
  localPort: number;
  externalTunnelActive: boolean;
  tunnelProvider: 'TELEBIT' | 'NGROK' | 'CLOUDFLARE';
  externalUrl: string;
  encryptionEnabled: boolean;
  autoConvertTTS: boolean;
  modelAliasing: boolean;
  crossProviderTokens: boolean;
  aiConfig: AIIntelligenceConfig;
  watchDuration: 'INDEFINITE' | '10M' | '30M' | '1H' | '4H';
  visionFps: number; 
  audioSamplingInterval: number;
  audioBurstDuration: number;
}

export interface SystemLog {
  id: string;
  timestamp: string;
  level: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG' | 'AI_ACTION';
  message: string;
  details?: string;
  component?: string;
}

export interface SystemStats {
  cpuLoad: number;
  gpuLoad: number;
  vramUsed: number;
  vramTotal: number;
  ramUsed: number;
  ramTotal: number;
  activeRequests: number;
  throughput: number;
}

export interface QueueItem {
  id: string;
  clientId: string;
  clientName: string;
  timestamp: string;
  modelId: string;
  priority: number;
}

export interface AlertThreshold {
  id: string;
  metric: 'GPU' | 'LATENCY' | 'CPU';
  value: number;
  enabled: boolean;
  channel: 'SLACK' | 'EMAIL' | 'WEBHOOK';
}

// Agent Orchestration Types
export type InteractionMode = 'text' | 'voice' | 'vision' | 'multimodal';
export type AgentType = 'master' | 'vision' | 'watchdog';
export type ThreatPriority = 'HIGH' | 'NORMAL';
export type VisionMode = 'auto' | 'on-demand' | 'paused';

export interface AgentMessage {
  id: string;
  from: AgentType;
  to: AgentType;
  type: 'command' | 'report' | 'query';
  payload: any;
  timestamp: number;
}

export interface CharacterCard {
  id: string;
  name: string;
  systemPrompt: string;
  voiceSettings?: {
    voiceId: string;
    language: string;
  };
  guardianRules?: string;
  createdAt: string;
  lastUsed: string;
}

export interface WatchdogStatus {
  overall: 'OK' | 'WARNING' | 'CRITICAL';
  ram: { percent: number; status: 'OK' | 'WARNING' | 'CRITICAL' };
  cpu: { percent: number; status: 'OK' | 'WARNING' | 'CRITICAL' };
  vram: { percent: number; status: 'OK' | 'WARNING' | 'CRITICAL' };
  services: {
    ollama: 'OK' | 'OFFLINE' | 'ERROR';
    tts: 'OK' | 'OFFLINE' | 'ERROR';
    asr: 'OK' | 'OFFLINE' | 'ERROR';
  };
  lastCheck: string;
}
